// src/lib/resources/data/ebooks/themes.ts

/**
 * @fileoverview Visual themes for ebook covers, cards, and PDF accents.
 * Each category gets a distinct gradient palette so catalog items are
 * visually distinguishable even before per-title OG art ships.
 */

/** Visual theme tokens for an ebook category. */
export interface EbookCoverTheme {
  /** Tailwind gradient stops (without `bg-gradient-to-br`) */
  gradient: string
  /** Short label shown on covers and badges */
  label: string
  /** Decorative emoji / glyph on the cover */
  glyph: string
  /** PDF accent color as RGB tuple */
  pdfAccent: readonly [number, number, number]
}

const THEMES: Record<string, EbookCoverTheme> = {
  creative_strategy: {
    gradient: 'from-violet-600 via-purple-600 to-indigo-900',
    label: 'Creative Strategy',
    glyph: '◈',
    pdfAccent: [109, 40, 217],
  },
  performance_marketing: {
    gradient: 'from-emerald-600 via-green-600 to-teal-900',
    label: 'Performance Marketing',
    glyph: '◎',
    pdfAccent: [5, 150, 105],
  },
  analytics: {
    gradient: 'from-blue-600 via-sky-600 to-cyan-900',
    label: 'Analytics & Metrics',
    glyph: '◉',
    pdfAccent: [37, 99, 235],
  },
}

const DEFAULT_THEME: EbookCoverTheme = {
  gradient: 'from-blue-600 via-indigo-600 to-violet-900',
  label: 'Marketing Playbook',
  glyph: '◆',
  pdfAccent: [59, 130, 246],
}

/**
 * Resolve the cover theme for an ebook category slug.
 *
 * @param {string} [category] - Ebook category id from data
 * @returns {EbookCoverTheme} Theme tokens for covers and PDF accents
 */
export function getEbookCoverTheme(category?: string): EbookCoverTheme {
  if (!category) return DEFAULT_THEME
  return THEMES[category] ?? DEFAULT_THEME
}
